class AudioPlayer extends HTMLElement {
  constructor() {
    super();
    this.audio = this.querySelector("audio");
    this.play_button = this.querySelector(".audio-player__play-btn");
    this.mute_button = this.querySelector(".audio-player__mute-btn");
    this.track = this.querySelector(".audio-player__track");
    this.fill = this.querySelector(".audio-player__fill");
    this.handle = this.querySelector(".audio-player__handle");
    this.currentEl = this.querySelector(".audio-player__current");
    this.durationEl = this.querySelector(".audio-player__duration");

    this.dragging = false;
    this.wasPlaying = false;

    this.events = [
      {
        el: this.audio,
        type: [
          "timeupdate",
          "loadmetadata",
          "durationchange",
          "ended",
          "play",
          "pause",
          "volumechange",
        ],
        callback: this.audioEventsHandler.bind(this),
      },
      {
        el: this.play_button,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.mute_button,
        type: "click",
        callback: this.clickHandler.bind(this),
      },
      {
        el: this.track,
        type: "pointerdown",
        callback: this.pointerHandler.bind(this),
      },
      {
        el: document,
        type: ["pointermove", "pointerup", "pointercancel"],
        callback: this.pointerHandler.bind(this),
      },
    ];
  }
  connectedCallback() {
    if (!this.audio) {
      this.style.display = "none";
      return;
    }
    handleEventListeners(this.events, "add");
    if (this.audio.readyState >= 1) this.onMetadata();
  }

  disconnectedCallback() {
    handleEventListeners(this.events, "remove");
  }

  clickHandler(e) {
    if (e.target.closest(".audio-player__play-btn")) {
      if (this.audio.paused) {
        this.audio.play();
      } else {
        this.audio.pause();
      }
    }
    if (e.target.closest(".audio-player__mute-btn")) {
      this.audio.muted = !this.audio.muted;
    }
  }

  pointerHandler(e) {
    if (e.type === "pointerdown") {
      if (e.button !== 0) return;
      e.preventDefault();
      this.dragging = true;
      this.wasPlaying = !this.audio.paused;
      if (this.wasPlaying) this.audio.pause();
      this.seek(e);
    }
    if (e.type === "pointermove") {
      if (!this.dragging) return;
      if (e.cancelable) e.preventDefault();
      this.seek(e);
    }
    if (e.type === "pointerup" || e.type === "pointercancel") {
      if (!this.dragging) return;
      this.dragging = false;
      if (e.type === "pointerup") this.seek(e);
      if (this.wasPlaying) this.audio.play();
    }
  }

  audioEventsHandler(e) {
    if (e.type === "ended") {
      this.removeAttribute("playing");
      this.audio.currentTime = 0;
      this.setProgress(0);
      this.currentEl.textContent = this.formatTime(0);
      this.track.setAttribute("aria-valuenow", 0);
    }
    if (e.type === "play") {
      this.setAttribute("playing", "");
      this.play_button.setAttribute("aria-label", "Pause");
    }
    if (e.type === "pause") {
      this.removeAttribute("playing");
      this.play_button.setAttribute("aria-label", "Play");
    }
    if (e.type === "volumechange") {
      const muted = this.audio.muted || this.audio.volume === 0;
      this.toggleAttribute("muted", muted);
      this.mute_button.setAttribute("aria-label", muted ? "Unmute" : "Mute");
    }
    if (e.type === "timeupdate") {
      if (this.dragging) return;
      const pct = this.audio.duration ? this.audio.currentTime / this.audio.duration : 0;
      this.setProgress(pct);
      this.currentEl.textContent = this.formatTime(this.audio.currentTime);
      this.track.setAttribute("aria-valuenow", Math.floor(this.audio.currentTime));
    }
    if (e.type === "loadmetadata" || e.type === "durationchange") {
      this.onMetadata();
    }
  }

  onMetadata() {
    this.durationEl.textContent = this.formatTime(this.audio.duration);
    this.track.setAttribute("aria-valuemax", Math.floor(this.audio.duration || 0));
  }

  setProgress(pct) {
    const p = Math.max(0, Math.min(1, pct)) * 100;
    this.fill.style.width = `${p}%`;
    this.handle.style.left = `${p}%`;
  }

  seek(e) {
    const rect = this.track.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    this.setProgress(pct);
    const newTime = pct * (this.audio.duration || 0);
    this.audio.currentTime = newTime;
    this.currentEl.textContent = this.formatTime(newTime);
    this.track.setAttribute("aria-valuenow", Math.floor(newTime));
  }

  formatTime(secs) {
    if (!isFinite(secs)) return "00:00";
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60);
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
}
customElements.define("audio-player", AudioPlayer);
